import { useEffect, useState } from 'react'
import { Plus, Search, Filter, Edit, Trash2, Eye, Calendar, Globe, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import useTaxRateStore from '@/stores/taxRateStore'
import useCountryStore from '@/stores/countryStore'
import useRegionStore from '@/stores/regionStore'
import { useNavigate } from 'react-router-dom'

const TaxRatesPage = () => {
  const navigate = useNavigate()
  
  const {
    taxRates,
    loading,
    error,
    pagination,
    fetchTaxRates,
    deleteTaxRate,
    setFilters,
    clearError
  } = useTaxRateStore()

  const { countries, fetchCountries } = useCountryStore()
  const { regions, fetchRegions } = useRegionStore()

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<string>('')
  const [selectedRegion, setSelectedRegion] = useState<string>('')
  const [showActiveOnly, setShowActiveOnly] = useState(true)

  useEffect(() => {
    fetchTaxRates()
    fetchCountries()
    fetchRegions()
  }, [])

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setFilters({
        search: searchTerm || undefined,
        country_id: selectedCountry || undefined,
        region_id: selectedRegion || undefined,
        enabled: showActiveOnly ? true : undefined
      })
    }, 300)

    return () => clearTimeout(debounceTimer)
  }, [searchTerm, selectedCountry, selectedRegion, showActiveOnly])

  const handleDelete = async (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce taux de taxe ? Cette action est irréversible.')) {
      try {
        await deleteTaxRate(id)
      } catch (error) {
        console.error('Error deleting tax rate:', error)
      }
    }
  }

  const getCountryName = (countryId: string) => {
    const country = countries.find(c => c.id === countryId)
    return country ? `${country.name} (${country.code})` : countryId
  }

  const getRegionName = (regionId: string | null) => {
    if (!regionId) return 'National'
    const region = regions.find(r => r.id === regionId)
    return region ? region.name : regionId
  }

  const getStatusBadge = (enabled: boolean, effectiveFrom: string, effectiveTo: string | null) => {
    const now = new Date()
    const from = new Date(effectiveFrom)
    const to = effectiveTo ? new Date(effectiveTo) : null

    if (!enabled) {
      return <Badge variant="destructive">Désactivé</Badge>
    }

    if (now < from) {
      return <Badge variant="outline">Planifié</Badge>
    }

    if (to && now > to) {
      return <Badge variant="secondary">Expiré</Badge>
    }

    return <Badge variant="default">Actif</Badge>
  }

  const getTypeBadge = (type: string) => {
    const typeMap: Record<string, string> = {
      vat: 'TVA',
      sales_tax: 'Taxe de vente',
      service_tax: 'Taxe de service',
      excise: 'Accise',
      custom: 'Personnalisé'
    }

    const colorMap: Record<string, any> = {
      vat: 'default',
      sales_tax: 'secondary',
      service_tax: 'outline',
      excise: 'destructive',
      custom: 'default'
    }

    return (
      <Badge variant={colorMap[type] || 'default'}>
        {typeMap[type] || type}
      </Badge>
    )
  }

  const formatPercentage = (rate: number | string) => {
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate
    if (isNaN(numRate)) return '0.00%'
    return `${numRate.toFixed(2)}%`
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (loading && taxRates.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Taux de Taxe</h1>
            <p className="text-muted-foreground">
              Gérez les taux de taxe pour les pays et régions
            </p>
          </div>
          <Button onClick={() => navigate('/tax-rates/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau Taux de Taxe
          </Button>
        </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-4" onClick={clearError}>
              Fermer
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
          <CardDescription>
            Filtrez les taux de taxe par pays, région, statut, etc.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Recherche</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nom de la taxe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Pays</label>
              <select
                className="w-full p-2 border rounded-md"
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
              >
                <option value="">Tous les pays</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Région</label>
              <select
                className="w-full p-2 border rounded-md"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                disabled={!selectedCountry}
              >
                <option value="">Toutes les régions</option>
                <option value="null">National (pas de région)</option>
                {regions
                  .filter(region => !selectedCountry || region.country_id === selectedCountry)
                  .map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Statut</label>
              <select
                className="w-full p-2 border rounded-md"
                value={showActiveOnly ? 'active' : 'all'}
                onChange={(e) => setShowActiveOnly(e.target.value === 'active')}
              >
                <option value="active">Actifs seulement</option>
                <option value="all">Tous</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Taux de Taxe</CardTitle>
              <CardDescription>
                {pagination.total} taux de taxe trouvés
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => fetchTaxRates()}>
                <Filter className="mr-2 h-4 w-4" />
                Actualiser
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Taux</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pays</TableHead>
                  <TableHead>Région</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taxRates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {loading ? 'Chargement...' : 'Aucun taux de taxe trouvé'}
                    </TableCell>
                  </TableRow>
                ) : (
                  taxRates.map((taxRate) => (
                    <TableRow key={taxRate.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-2">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <span>{taxRate.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-lg">
                          {formatPercentage(taxRate.rate)}
                        </span>
                      </TableCell>
                      <TableCell>{getTypeBadge(taxRate.type)}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span>{getCountryName(taxRate.country_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{getRegionName(taxRate.region_id || null)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">
                              Du {formatDate(taxRate.effective_from)}
                            </span>
                          </div>
                          {taxRate.effective_to && (
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm">
                                Au {formatDate(taxRate.effective_to)}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(
                          taxRate.enabled,
                          taxRate.effective_from,
                          taxRate.effective_to || null
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/tax-rates/${taxRate.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/tax-rates/${taxRate.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(taxRate.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {pagination.page} sur {pagination.pages} • {pagination.total} éléments
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ page: pagination.page - 1 })}
                  disabled={pagination.page <= 1}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ page: pagination.page + 1 })}
                  disabled={pagination.page >= pagination.pages}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default TaxRatesPage
